import { produce } from 'immer';
import { createReducer } from 'typesafe-actions';

import { resetStore } from '../actions';
import type { GlobalAction } from '../actions';
import { nextHydrate } from '../hydrate';
import { reconcileState, replaceById, replaceByIdFromArray } from '../utils';

import {
  accountByUuidAction,
  accountCreateRequestAction,
  accountDeactivationRequestAction,
  accountRequestAction,
  accountUpdatePayoutSettingsRequestAction,
  accountUpdateRequestAction,
  accountsSelfServiceRequestAction,
  createAccountDeactivationsAction,
  deleteAccountDeactivationAction,
  toggleCreateAccount,
  toggleDuplicateAccount,
} from './actions';
import type { AccountAction, AccountState } from './types';

export const initialAccountState: AccountState = {
  byUuid: {},
  createDrawerVisible: false,
  deactivationRows: [],
  duplicateUuid: '',
  haveAllAccountsBeenRequested: false,
  haveAllAccountsDeactivationBeenRequested: false,
  rows: [],
  rowsSelfService: [],
};

export const accountReducer = createReducer<
  AccountState,
  AccountAction | GlobalAction
>(initialAccountState)
  .handleAction(resetStore, () => initialAccountState)
  .handleAction(nextHydrate, (state, { payload }) => ({
    ...state,
    ...reconcileState(payload.accounts, initialAccountState),
  }))
  .handleAction(accountRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.haveAllAccountsBeenRequested = true;
      draftState.rows = payload;
    })
  )
  .handleAction(accountByUuidAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.byUuid[payload.uuid] = payload;
      draftState.rows = replaceById(state.rows, payload);
    })
  )
  .handleAction(accountCreateRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.byUuid[payload.uuid] = payload;
      draftState.rows = replaceById(state.rows, payload);
    })
  )
  .handleAction(toggleDuplicateAccount, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.createDrawerVisible = !state.createDrawerVisible;
      draftState.duplicateUuid = payload;
    })
  )
  .handleAction(toggleCreateAccount, (state) =>
    produce(state, (draftState) => {
      draftState.createDrawerVisible = !state.createDrawerVisible;
      draftState.duplicateUuid = '';
    })
  )
  .handleAction(accountUpdateRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.rows = replaceById(state.rows, payload);
    })
  )
  .handleAction(
    createAccountDeactivationsAction.success,
    (state, { payload }) =>
      produce(state, (draftState) => {
        draftState.deactivationRows = state.deactivationRows.concat(payload);
      })
  )
  .handleAction(
    accountDeactivationRequestAction.success,
    (state, { payload }) =>
      produce(state, (draftState) => {
        draftState.haveAllAccountsDeactivationBeenRequested = true;
        draftState.deactivationRows = payload;
      })
  )
  .handleAction(deleteAccountDeactivationAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.deactivationRows = replaceByIdFromArray(
        state.deactivationRows,
        payload
      );
    })
  )
  .handleAction(
    accountsSelfServiceRequestAction.success,
    (state, { payload }) =>
      produce(state, (draftState) => {
        draftState.rowsSelfService = payload;
      })
  )
  .handleAction(
    accountUpdatePayoutSettingsRequestAction.success,
    (state, { payload }) =>
      produce(state, (draftState) => {
        draftState.byUuid[payload.uuid] = payload;
        draftState.rows = replaceById(state.rows, payload);
      })
  );
